import type {
  AgencyModel,
  AppFeatures,
  FeatureToggles as FeatureTogglesModel,
} from '@urbanx/agx.agencies.client'
import { LicenceDetailsState } from '@urbanx/agx.agencies.client'
import type { DropdownOption, IColumnConfig } from '@urbanx/agx.ui-components2'

export type FeatureKey = keyof AppFeatures
export type FeatureLevel = 'global' | 'agency'
export type FeatureState = LicenceDetailsState
export type FeatureRows = Partial<Record<FeatureState, AppFeatures>>
export type NestedFeatureRows = Record<string, FeatureRows>

interface FeatureDefinition {
  key: FeatureKey
  label: string
}

const allStates: FeatureState[] = [
  LicenceDetailsState.ACT,
  LicenceDetailsState.NSW,
  LicenceDetailsState.NT,
  LicenceDetailsState.QLD,
  LicenceDetailsState.SA,
  LicenceDetailsState.TAS,
  LicenceDetailsState.WA,
  LicenceDetailsState.VIC,
]

export const featureDefinitions: FeatureDefinition[] = [
  { key: 'purchaseTitleDocuments', label: 'Purchase title documents' },
  { key: 'requestMarketingFunds', label: 'Request marketing funds' },
  { key: 'automateLiveListings', label: 'Automate live listings' },
  { key: 'submitListingToRex', label: 'Submit listing to Rex' },
  { key: 'submitContractToRex', label: 'Submit contract to Rex' },
  { key: 'addMarketingToLiveListing', label: 'Add marketing to live listing' },
  {
    key: 'hideLeaderBoardForLastFinancialYear',
    label: 'Hide leaderboard for last financial year',
  },
  { key: 'automateEmailListings', label: 'Automate email listings' },
  { key: 'automateEmailContracts', label: 'Automate email contracts' },
  { key: 'assignCampaignFlowAdmin', label: 'Assign Campaign Flow admin' },
  { key: 'enableLaunchPad', label: 'Enable LaunchPad' },
  { key: 'identityVerification', label: 'Identity verification' },
  { key: 'enableNswContractExchange', label: 'Enable NSW Contract Exchange' },
]

export const tableColumns: (string | IColumnConfig)[] = [
  { name: 'Feature', maxColWidth: '360px' },
  { name: 'Enabled', shrink: true },
  { name: 'Current value', shrink: true },
]

export const levelSegments = [
  { title: 'State', value: 'global' },
  { title: 'Agency', value: 'agency' },
]

export const stateOptions = allStates.map((state) => ({
  label: state,
  value: state,
}))

export const createEmptyAppFeatures = (): AppFeatures => ({
  purchaseTitleDocuments: false,
  requestMarketingFunds: false,
  automateLiveListings: false,
  submitListingToRex: false,
  submitContractToRex: false,
  addMarketingToLiveListing: false,
  hideLeaderBoardForLastFinancialYear: false,
  automateEmailListings: false,
  automateEmailContracts: false,
  assignCampaignFlowAdmin: false,
  enableLaunchPad: false,
  identityVerification: false,
  enableNswContractExchange: false,
})

export const cloneAppFeatures = (features?: AppFeatures): AppFeatures => ({
  ...createEmptyAppFeatures(),
  ...(features ?? {}),
})

export const createEmptyFeatureToggles = (): FeatureTogglesModel => ({
  globalFeatures: {},
  agencyFeatures: {},
  agentFeatures: {},
})

const cloneFeatureRows = (rows?: FeatureRows): FeatureRows =>
  Object.fromEntries(
    Object.entries(rows ?? {}).map(([state, features]) => [
      state,
      cloneAppFeatures(features),
    ])
  ) as FeatureRows

const cloneNestedFeatureRows = (rows?: NestedFeatureRows): NestedFeatureRows =>
  Object.fromEntries(
    Object.entries(rows ?? {}).map(([id, stateRows]) => [
      id,
      cloneFeatureRows(stateRows),
    ])
  )

export const cloneFeatureToggles = (
  featureToggles?: FeatureTogglesModel
): FeatureTogglesModel => ({
  globalFeatures: cloneFeatureRows(
    featureToggles?.globalFeatures as FeatureRows
  ),
  agencyFeatures: cloneNestedFeatureRows(
    featureToggles?.agencyFeatures as NestedFeatureRows
  ),
  agentFeatures: cloneNestedFeatureRows(
    featureToggles?.agentFeatures as NestedFeatureRows
  ),
})

export const getSelectedAgency = (
  agencies: AgencyModel[] | undefined,
  selectedAgencyId: string
) => (agencies ?? []).find((agency) => agency.id === selectedAgencyId)

export const getAgencyStates = (agency?: AgencyModel): FeatureState[] =>
  (agency?.states ?? [])
    .map((state) => String(state) as FeatureState)
    .filter((state) => allStates.includes(state))

export const getAgencyOptions = (
  agencies: AgencyModel[] | undefined
): DropdownOption[] =>
  (agencies ?? [])
    .filter((agency) => agency.id)
    .map((agency) => ({
      label: agency.name ?? 'Unnamed agency',
      value: agency.id!,
    }))

export const getRawFeatures = (
  featureToggles: FeatureTogglesModel,
  level: FeatureLevel,
  state: FeatureState,
  agencyId: string
): AppFeatures => {
  if (level === 'global') {
    return cloneAppFeatures(
      (featureToggles.globalFeatures as FeatureRows)?.[state]
    )
  }

  return cloneAppFeatures(
    (featureToggles.agencyFeatures as NestedFeatureRows)?.[agencyId]?.[state]
  )
}

const hasEnabledFeature = (features?: AppFeatures) =>
  featureDefinitions.some((feature) => features?.[feature.key] === true)

const pruneFeatureRows = (rows?: FeatureRows): FeatureRows =>
  Object.fromEntries(
    Object.entries(rows ?? {})
      .filter(([, features]) => hasEnabledFeature(features))
      .map(([state, features]) => [state, cloneAppFeatures(features)])
  ) as FeatureRows

const pruneNestedFeatureRows = (rows?: NestedFeatureRows): NestedFeatureRows =>
  Object.fromEntries(
    Object.entries(rows ?? {})
      .map(([id, stateRows]) => [id, pruneFeatureRows(stateRows)] as const)
      .filter(([, stateRows]) => Object.keys(stateRows).length > 0)
  )

export const pruneBlankRows = (
  featureToggles?: FeatureTogglesModel
): FeatureTogglesModel => ({
  globalFeatures: pruneFeatureRows(
    featureToggles?.globalFeatures as FeatureRows
  ),
  agencyFeatures: pruneNestedFeatureRows(
    featureToggles?.agencyFeatures as NestedFeatureRows
  ),
  agentFeatures: pruneNestedFeatureRows(
    featureToggles?.agentFeatures as NestedFeatureRows
  ),
})
